// Per-module form config for the "edit assessment module" popup. Each module's
// /questions schema, its current-selection location in /session, and its
// /calculate input field differ by family — all verified against the live API.
//
// Modes:
//   'mcq'      — QUESTIONS[] each single-select -> { answers: { Qn: optionIndex } }
//   'sections' — one or more option groups, each single or multi select

// multi-select group: session stores an array (of objects with `id`, or bare id
// strings for red flags); /calculate wants an array of ids.
const multi = (qKey, sessionKey, calcField, title) => ({
  type: "multi",
  qKey,
  calcField,
  title,
  current: (m) =>
    (Array.isArray(m && m[sessionKey]) ? m[sessionKey] : [])
      .map((x) => (x && x.id ? x.id : x))
      .filter(Boolean),
});

// single-select group stored at the top level of the session module (stool).
const singleTop = (qKey, sessionKey, calcField, title) => ({
  type: "single",
  qKey,
  calcField,
  title,
  current: (m) => (m && m[sessionKey] && m[sessionKey].id) || null,
});

// single-select group nested under session.selected_single[prefix] (touch, urine).
const singleNested = (qKey, prefix, calcField, title) => ({
  type: "single",
  qKey,
  calcField,
  title,
  current: (m) =>
    (m &&
      m.selected_single &&
      m.selected_single[prefix] &&
      m.selected_single[prefix].id) ||
    null,
});

const checklist = (title) => multi("ITEMS", "selected_items", "selected_ids", title);

export const MODULE_FORMS = {
  // Single-select MCQ
  agni: { mode: "mcq" },
  kostha: { mode: "mcq" },
  ojas: { mode: "mcq" },

  // Item checklists
  ama: { mode: "sections", sections: [checklist("Signs")] },
  satva: { mode: "sections", sections: [checklist("Signs")] },
  satmya: { mode: "sections", sections: [checklist("Signs")] },
  nidana: {
    mode: "sections",
    sections: [checklist("Causative factors")],
    // AH-05 ("Excess of one single taste") carries a follow-up question. The
    // engine requires followup_choice on every call — an index when AH-05 is
    // ticked, null otherwise — so the key is always sent (see buildPayload).
    followup: {
      triggerId: "AH-05",
      itemsKey: "ITEMS",
      calcField: "followup_choice",
      title: "Which taste or food group dominates your diet most days?",
    },
  },

  // Per-organ symptom checklists (netra/shrotra/ghrana/tvak). Values are the
  // exact symptom label strings; result -> { selections: { organ: [labels] } }.
  indriya: { mode: "indriya" },

  // Body + Face items (+ gates preserved from the session)
  akriti: {
    mode: "sections",
    extra: (m) => ({ gate_a: !!(m && m.gate_a), gate_b: !!(m && m.gate_b) }),
    sections: [
      multi("BD_ITEMS", "selected_bd", "selected_bd", "Body signs"),
      multi("FC_ITEMS", "selected_fc", "selected_fc", "Facial & neurological signs"),
    ],
  },

  // Exam groups
  stool: {
    mode: "sections",
    sections: [
      singleTop("COLOUR_OPTIONS", "selected_colour", "colour_idx", "Colour"),
      singleTop("SMELL_OPTIONS", "selected_smell", "smell_idx", "Smell"),
      multi("CONTENTS_ITEMS", "selected_contents", "contents_selected", "Contents"),
      singleTop("POST_DEF_OPTIONS", "selected_post_def", "post_def_idx", "After defecation"),
    ],
  },
  touch: {
    mode: "sections",
    sections: [
      singleNested("TEMPERATURE_OPTIONS", "temperature", "temperature_idx", "Temperature"),
      singleNested("TEXTURE_OPTIONS", "texture", "texture_idx", "Texture"),
      singleNested("MOBILITY_OPTIONS", "mobility", "mobility_idx", "Mobility"),
      singleNested("PAIN_OPTIONS", "pain", "pain_idx", "Pain"),
      singleNested("MUSCLE_OPTIONS", "muscle", "muscle_idx", "Muscle"),
      multi("SWELLING_ITEMS", "selected_swelling", "swelling_selected", "Swelling"),
      multi("ABDOMEN_ITEMS", "selected_abdomen", "abdomen_selected", "Abdomen"),
      multi("VATA_SIGNS_ITEMS", "selected_vata_signs", "vata_signs_selected", "Vāta signs"),
      multi("RED_FLAG_ITEMS", "red_flag_items", "red_flag_selected", "Red flags"),
    ],
  },
  urine: {
    mode: "sections",
    sections: [
      singleNested("COLOUR_OPTIONS", "colour", "colour_idx", "Colour"),
      singleNested("SMELL_OPTIONS", "smell", "smell_idx", "Smell"),
      singleNested("FREQUENCY_OPTIONS", "frequency", "frequency_idx", "Frequency"),
      singleNested("VOLUME_OPTIONS", "volume", "volume_idx", "Volume"),
      singleNested("PAIN_OPTIONS", "pain", "pain_idx", "Pain"),
      multi("APPEARANCE_ITEMS", "selected_appearance", "appearance_selected", "Appearance"),
      multi("URGE_ITEMS", "selected_urge", "urge_selected", "Urgency"),
      multi("OTHER_ITEMS", "selected_other", "other_selected", "Other"),
      // urine red flags persist to zone1_selected (URF-*), not red_flag_selected
      multi("RED_FLAG_ITEMS", "zone1_selected", "zone1_selected", "Red flags"),
    ],
  },
};

// Modules whose parameter row opens the popup (indriya is edited inline per organ).
export const POPUP_MODULES = Object.keys(MODULE_FORMS);
